import { Link } from '@tanstack/react-router';

import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { fmtPeriod } from './pp-helpers';
import { PPRunGovernance } from './PPRunGovernance';
import { useAmortizationRuns } from '../../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';

// Export columns for the journal-history tab.
const JOURNAL_EXPORT_COLS: ExportColumn<{
  period: string;
  voucherDate: Date | null;
  journalVoucherNo: string | null;
  contractsProcessed: number;
  totalAmount: unknown;
  postedBy: string | null;
}>[] = [
  { header: 'งวด', value: (r) => r.period, width: 10 },
  {
    header: 'วันที่ประมวลผล',
    value: (r) => (r.voucherDate ? String(r.voucherDate).slice(0, 10) : ''),
    width: 16,
  },
  { header: 'เลขที่ JV (Batch)', value: (r) => r.journalVoucherNo ?? '', width: 22 },
  { header: 'จำนวนรายการ', value: (r) => r.contractsProcessed, width: 12 },
  { header: 'ยอดตัดจ่าย', value: (r) => Number(r.totalAmount), width: 16 },
  { header: 'ผู้ประมวลผล', value: (r) => r.postedBy ?? '', width: 18 },
  { header: 'สถานะ', value: (_r) => 'ผ่านรายการแล้ว', width: 16 },
];

interface JournalProps {
  companyId: string | undefined;
}

export function PPJournal({ companyId }: JournalProps) {
  const { data: runs = [] } = useAmortizationRuns(companyId);

  async function handleExport(): Promise<void> {
    await exportRows(
      `ประวัติตัดจ่าย_${todayStamp()}.xlsx`,
      'ประวัติการตัดจ่าย',
      JOURNAL_EXPORT_COLS,
      runs,
    );
  }

  return (
    <>
      <PPRunGovernance companyId={companyId} />
      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            ประวัติการรันตัดจ่ายค่าใช้จ่ายล่วงหน้า (JV ที่ผ่านรายการ)
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={() => void handleExport()}>
              <Icon name="download" size={12} /> Export
            </button>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>งวด</th>
              <th>วันที่ประมวลผล</th>
              <th>เลขที่ JV (Batch)</th>
              <th className="num">จำนวนรายการ</th>
              <th className="num">ยอดตัดจ่าย</th>
              <th>ผู้ประมวลผล</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '24px 0' }}
                >
                  ยังไม่มีประวัติการตัดจ่าย
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <tr key={r.period}>
                  <td>
                    <b>{fmtPeriod(r.period)}</b>
                  </td>
                  <td>
                    {r.voucherDate ? (
                      fmtDate(String(r.voucherDate).slice(0, 10))
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="code">
                    {r.journalVoucherNo ? (
                      <Link to="/journal" style={{ color: 'var(--brand)', fontWeight: 500 }}>
                        {r.journalVoucherNo}
                      </Link>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">{r.contractsProcessed}</td>
                  <td className="num tnum">
                    <b>{fmt(Number(r.totalAmount))}</b>
                  </td>
                  <td className="muted">{r.postedBy ?? '—'}</td>
                  <td>
                    <span className="badge positive">
                      <span className="bdot" />
                      ผ่านรายการแล้ว
                    </span>
                  </td>
                  <td>
                    <Link
                      to="/journal"
                      className="btn sm ghost"
                      style={{ height: 28, fontSize: 14 }}
                    >
                      ดู JV
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
